import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIMS = [['setting', 'Care setting'], ['size', 'Size'], ['region', 'Region'], ['care_group', 'Type of care']];
const pill = (on) => `text-xs font-medium rounded-full px-3 py-1 transition-colors ${on ? 'bg-[#00A79D] text-white' : 'text-slate-400 hover:text-white border border-slate-700'}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const Bars = ({ rows, total, pages, dim, open, setOpen }) => (
  <div className="space-y-2" data-testid="care-settings-bars">
    {rows.length === 0 && <p className="text-sm text-slate-500">No identified visitors in this period yet.</p>}
    {rows.map((r) => {
      const pct = total ? Math.round((100 * r.visitors) / total) : 0;
      return (
        <div key={r.label}>
          <button type="button" className="w-full text-left" onClick={() => setOpen(open === r.label ? null : r.label)} data-testid={`care-row-${r.label}`}>
            <div className="flex justify-between text-sm"><span className="text-slate-200">{r.label}</span>
              <span className="text-slate-400"><b className="text-white">{r.visitors}</b> people · {r.views} views</span></div>
            <div className="h-2 bg-slate-800 rounded mt-1 overflow-hidden"><div className="h-full bg-[#00A79D] rounded transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          </button>
          {dim === 'setting' && open === r.label && (
            <ul className="mt-2 ml-3 space-y-1 text-xs text-slate-400" data-testid={`care-pages-${r.label}`}>
              {(pages[r.label] || []).map((p) => <li key={p.path} className="flex justify-between gap-3"><span className="truncate">{p.path}</span><span>{p.views} views</span></li>)}
            </ul>
          )}
        </div>
      );
    })}
  </div>
);

const CompanyTable = ({ rows }) => {
  const navigate = useNavigate();
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto mt-6" data-testid="care-companies-table">
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-800 text-left">
          <th className="py-2 pr-3">Company</th><th className="pr-3">Setting</th><th className="pr-3">Size / region</th><th className="pr-3">Type of care</th><th className="pr-3">Views</th><th>Last visit</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.company_id || r.company || i} className="border-b border-slate-800/60 align-top" data-testid={`care-company-${i}`}>
              <td className="py-2 pr-3">
                {r.company_id ? <button type="button" onClick={() => navigate(`/admin/companies?open=${r.company_id}`)} className="text-[#00A79D] hover:underline font-medium text-left" data-testid={`care-company-link-${i}`}>{r.company}</button>
                  : <span className="text-slate-300">{r.company || 'Unknown'}</span>}
                <div className="text-xs text-slate-500">{r.people.join(', ')}</div>
                <div className="text-xs text-slate-600 truncate max-w-[260px]">{r.top_pages.join(' · ')}</div>
              </td>
              <td className="pr-3 text-slate-300">{r.setting}</td>
              <td className="pr-3 text-slate-400 text-xs">{r.size}<br />{r.region}</td>
              <td className="pr-3 text-slate-400 text-xs max-w-[200px]">{r.care_groups.join(', ') || 'Not known'}</td>
              <td className="pr-3 text-white">{r.views}</td>
              <td className="text-slate-400 text-xs whitespace-nowrap">{when(r.last_visit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CareSettingsCard = () => {
  const [days, setDays] = useState(30);
  const [dim, setDim] = useState('setting');
  const [open, setOpen] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); axios.get(`${API}/admin/site-analytics/care-settings?days=${days}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [days]);
  return (
    <Card className="bg-slate-900/50 border-slate-800 mb-6" data-testid="care-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-300 flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#00A79D]" /> Email click-through visitors by care setting</span>
          <span className="flex gap-1">{[30, 90].map((n) => <button key={n} type="button" className={pill(days === n)} onClick={() => setDays(n)} data-testid={`care-days-${n}`}>{n} days</button>)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!d ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          : d.error ? <p className="text-sm text-slate-500">Could not load care settings.</p> : (
            <>
              <p className="text-xs text-slate-500 mb-3" data-testid="care-identified">
                <b className="text-slate-300">{d.identified_visitors}</b> people identified from email clicks, out of {d.total_visitors.toLocaleString()} visitors. Every page they view from then on is counted against their company.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">{DIMS.map(([k, l]) => <button key={k} type="button" className={pill(dim === k)} onClick={() => { setDim(k); setOpen(null); }} data-testid={`care-dim-${k}`}>{l}</button>)}</div>
              {dim === 'care_group' && <p className="text-[11px] text-slate-500 mb-2">From CQC specialisms. A company can serve several types of care, so people may count more than once.</p>}
              <Bars rows={d.dimensions[dim] || []} total={d.identified_visitors} pages={d.top_pages} dim={dim} open={open} setOpen={setOpen} />
              <CompanyTable rows={d.companies} />
            </>
          )}
      </CardContent>
    </Card>
  );
};
